import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Package, AlertTriangle, Inbox, Newspaper, LogOut, TrendingDown, Wrench, ClipboardList } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { BUSINESS } from "../../lib/constants";
import { PartsManager } from "./PartsManager";
import { QuotesManager } from "./QuotesManager";
import { BlogManager } from "./BlogManager";
import { CustomOrdersManager } from "./CustomOrdersManager";

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("parts");
  const [restockSku, setRestockSku] = useState(null);

  const loadStats = () => api.get("/admin/stats").then((res) => setStats(res.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const handleRestock = (sku) => {
    setActiveTab("parts");
    setRestockSku(sku);
  };

  const cards = stats
    ? [
        { icon: Package, label: "Total Parts", value: stats.total_parts, testid: "stat-total-parts" },
        { icon: AlertTriangle, label: "Out of Stock", value: stats.out_of_stock, testid: "stat-out-of-stock" },
        { icon: TrendingDown, label: "Low Stock", value: stats.low_stock_count, testid: "stat-low-stock" },
        { icon: Inbox, label: "Pending Quotes", value: stats.pending_quotes, testid: "stat-pending-quotes" },
        { icon: ClipboardList, label: "Custom Orders", value: stats.pending_custom_orders, testid: "stat-pending-custom-orders" },
        { icon: Newspaper, label: "Blog Articles", value: stats.total_blog_posts, testid: "stat-blog-posts" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-obsidian pt-28 pb-20" data-testid="admin-dashboard-page">
      <Helmet><title>Admin Dashboard | Orion Services LLC</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-10">
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.25em] text-amber-light">Control Center</span>
            <h1 className="font-display text-3xl sm:text-4xl uppercase text-white mt-1">Welcome, {user?.name || "Admin"}</h1>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-button" className="border-amber-500/30 text-white hover:bg-amber-500/10">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5 mb-8">
          {cards.map((c) => (
            <div key={c.label} data-testid={c.testid} className="border border-amber-500/15 rounded-md p-5 bg-carbon">
              <c.icon className="h-5 w-5 text-amber-light mb-3" />
              <div className="font-display text-3xl text-white">{c.value}</div>
              <div className="text-xs font-mono uppercase text-zinc-500 mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        {stats && stats.low_stock_count > 0 && (
          <div data-testid="admin-low-stock-alert-banner" className="mb-12 border border-amber-500/40 bg-amber-950/30 rounded-md p-5">
            <div className="flex items-center gap-3 mb-3">
              <TrendingDown className="h-5 w-5 text-amber-light shrink-0" />
              <p className="text-amber-light font-semibold text-sm uppercase tracking-wide">
                {stats.low_stock_count} part{stats.low_stock_count > 1 ? "s" : ""} running low on stock
              </p>
            </div>
            <ul className="space-y-2">
              {stats.low_stock_parts.map((p) => (
                <li key={p.sku} data-testid={`admin-low-stock-item-${p.sku}`} className="flex items-center justify-between gap-4 text-sm text-zinc-300 border-t border-amber-500/10 pt-2 first:border-t-0 first:pt-0">
                  <span>{p.name} <span className="text-zinc-500 font-mono">({p.sku})</span> — {p.stock_quantity} left</span>
                  <Button
                    size="sm"
                    onClick={() => handleRestock(p.sku)}
                    data-testid={`admin-restock-btn-${p.sku}`}
                    className="bg-amber-light text-obsidian hover:bg-white shrink-0"
                  >
                    <Wrench className="h-3.5 w-3.5 mr-1.5" /> Restock
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-carbon border border-amber-500/10">
            <TabsTrigger value="parts" data-testid="admin-tab-parts">Parts</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Quotes</TabsTrigger>
            <TabsTrigger value="custom-orders" data-testid="admin-tab-custom-orders">Custom Orders</TabsTrigger>
            <TabsTrigger value="blog" data-testid="admin-tab-blog">Blog</TabsTrigger>
          </TabsList>
          <TabsContent value="parts" className="mt-6">
            <PartsManager onChange={loadStats} focusSku={restockSku} onFocusHandled={() => setRestockSku(null)} />
          </TabsContent>
          <TabsContent value="quotes" className="mt-6">
            <QuotesManager onChange={loadStats} />
          </TabsContent>
          <TabsContent value="custom-orders" className="mt-6">
            <CustomOrdersManager onChange={loadStats} />
          </TabsContent>
          <TabsContent value="blog" className="mt-6">
            <BlogManager onChange={loadStats} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
